export const globalCapDetailPages = {
  "dedicated-staffing": {
    hero: {
      eyebrow: "DEDICATED STAFFING",
      headline: "The Right People, Aligned with Your Business.",
      description:
        "Build additional capacity with dedicated professionals selected around your role requirements, workflows, and business priorities. Qualitas Global can work with you to define the skills and team structure your operation needs.",
      primaryCta: { label: "Discuss Your Staffing Needs", href: "/contact-us" },
      secondaryCta: { label: "Explore Our Approach" },
    },
    overview: {
      heading: "Extend Your Team with Purpose",
      body: [
        "When internal teams need additional skills or capacity, dedicated staffing offers a way to bring in focused support without treating every requirement as a separate, one-off task. Define the roles, responsibilities, and working arrangements that fit your organization.",
      ],
    },
    offerings: {
      title: "Staffing Solutions",
      items: [
        {
          title: "Dedicated Team Support",
          description:
            "Add professionals to support defined functions, projects, or ongoing workloads.",
        },
        {
          title: "Specialized Role Support",
          description:
            "Identify role-specific skills based on the responsibilities and experience your requirements call for.",
        },
        {
          title: "Flexible Team Capacity",
          description:
            "Discuss the team size and engagement structure that fit your current workload and anticipated needs.",
        },
        {
          title: "Project-Based Staffing",
          description:
            "Explore staffing support for a defined project, initiative, or period of increased demand.",
        },
      ],
    },
    process: {
      title: "How It Works",
      items: [
        {
          step: "Step 1",
          title: "Role Discovery",
          description:
            "Define responsibilities, required skills, experience, and working arrangements.",
        },
        {
          step: "Step 2",
          title: "Candidate Alignment",
          description:
            "Review candidate profiles against the agreed role criteria and selection process.",
        },
        {
          step: "Step 3",
          title: "Team Onboarding",
          description:
            "Coordinate onboarding, role expectations, and communication with the relevant stakeholders.",
        },
        {
          step: "Step 4",
          title: "Ongoing Alignment",
          description:
            "Review staffing needs and address changes in scope or team requirements.",
        },
      ],
    },
    benefits: {
      title: "Benefits",
      items: [
        {
          title: "Closer Role Alignment",
          description:
            "Set clear expectations for responsibilities and required skills.",
        },
        {
          title: "Additional Capacity",
          description:
            "Add support where workloads or project requirements call for it.",
        },
        {
          title: "Defined Collaboration",
          description:
            "Agree on communication, reporting, and working arrangements at the outset.",
        },
        {
          title: "Adaptable Engagement",
          description:
            "Review the arrangement as priorities and requirements change.",
        },
      ],
    },
    closing: {
      headline: "Build a Team Around Your Goals.",
      body: "Share the roles, skills, and capacity you need. Let's discuss a staffing approach that fits your organization.",
      cta: { label: "Talk to Our Staffing Team", href: "/contact-us" },
    },
  },

  kpo: {
    hero: {
      eyebrow: "KNOWLEDGE PROCESS OUTSOURCING (KPO)",
      headline: "Specialized Knowledge. Structured Delivery.",
      description:
        "Knowledge Process Outsourcing supports work that relies on specialized knowledge, analysis, and informed execution. Qualitas Global can work with your team to define the processes, expertise, and delivery approach required for your business.",
      primaryCta: { label: "Discuss Your KPO Requirements", href: "/contact-us" },
      secondaryCta: { label: "Explore Global Capability Services" },
    },
    overview: {
      heading: "Support for Knowledge-Intensive Work",
      body: [
        "Some business processes require more than routine execution: they depend on subject knowledge, careful analysis, and consistent handling of information. A clearly scoped KPO engagement can help organizations structure this work around defined requirements and outcomes.",
      ],
    },
    offerings: {
      title: "Potential KPO Work Areas",
      items: [
        {
          title: "Research and Information Support",
          description:
            "Structured research and information gathering against an agreed brief.",
        },
        {
          title: "Data and Document Analysis",
          description:
            "Organizing, reviewing, and extracting relevant information from agreed data or document sources.",
        },
        {
          title: "Reporting and Knowledge Support",
          description:
            "Preparing structured reports, summaries, or knowledge materials according to defined requirements.",
        },
        {
          title: "Specialized Process Support",
          description:
            "Additional knowledge-intensive workflows based on the client's domain and agreed scope.",
        },
      ],
    },
    process: {
      title: "Delivery Approach",
      items: [
        {
          step: "Step 1",
          title: "Define the Work",
          description:
            "Identify the process, knowledge requirements, source materials, and expected outputs.",
        },
        {
          step: "Step 2",
          title: "Set Quality Criteria",
          description:
            "Agree on review standards, documentation, escalation paths, and acceptance criteria.",
        },
        {
          step: "Step 3",
          title: "Execute the Process",
          description:
            "Carry out the agreed work using the approved workflow and communication process.",
        },
        {
          step: "Step 4",
          title: "Review and Improve",
          description:
            "Assess deliverables and refine the workflow where appropriate.",
        },
      ],
    },
    benefits: {
      title: "Potential Benefits",
      items: [
        {
          title: "Focused Internal Teams",
          description:
            "Allow internal specialists to prioritize work that must remain in-house.",
        },
        {
          title: "Defined Outputs",
          description:
            "Set expectations for deliverables, documentation, and review.",
        },
        {
          title: "Process Consistency",
          description:
            "Use agreed workflows and quality criteria across recurring tasks.",
        },
        {
          title: "Scope-Based Support",
          description:
            "Shape the engagement around the specific work and expertise required.",
        },
      ],
    },
    closing: {
      headline: "Have a Knowledge-Intensive Process to Support?",
      body: "Tell us about the work, expertise, and outputs you need. We can discuss the scope and an appropriate delivery model.",
      cta: { label: "Discuss KPO Services", href: "/contact-us" },
    },
  },

  bpo: {
    hero: {
      eyebrow: "BUSINESS PROCESS OUTSOURCING (BPO)",
      headline: "Streamline Business Processes. Focus on What Matters.",
      description:
        "Business Process Outsourcing allows organizations to assign agreed operational processes to an external delivery partner. Define the tasks, responsibilities, and service expectations so internal teams can focus on their core business priorities.",
      primaryCta: { label: "Discuss Your BPO Requirements", href: "/contact-us" },
      secondaryCta: { label: "Explore Services" },
    },
    overview: {
      heading: "Operational Support Built Around Your Process",
      body: [
        "A well-defined BPO engagement starts with a clear understanding of the work, its inputs and outputs, the people involved, and the required service standards. Qualitas Global can discuss your requirements and determine whether a suitable process-support model can be defined.",
      ],
    },
    offerings: {
      title: "Potential BPO Work Areas",
      items: [
        {
          title: "Business Administration Support",
          description:
            "Support for agreed administrative workflows and recurring operational tasks.",
        },
        {
          title: "Data and Document Processing",
          description:
            "Structured handling, review, or entry of information according to defined procedures.",
        },
        {
          title: "Customer or Internal Support Processes",
          description:
            "Process support for approved customer-facing or internal workflows, where offered.",
        },
        {
          title: "Workflow Coordination",
          description:
            "Support for defined process steps, status tracking, and operational handoffs.",
        },
      ],
    },
    process: {
      title: "Delivery Approach",
      items: [
        {
          step: "Step 1",
          title: "Map the Process",
          description:
            "Document the workflow, inputs, outputs, responsibilities, and exceptions.",
        },
        {
          step: "Step 2",
          title: "Agree on Standards",
          description:
            "Define quality criteria, turnaround expectations, reporting, and escalation routes.",
        },
        {
          step: "Step 3",
          title: "Set Up Delivery",
          description:
            "Confirm team responsibilities, communication, and the approved working process.",
        },
        {
          step: "Step 4",
          title: "Monitor and Review",
          description:
            "Review agreed measures and address process issues or changes in scope.",
        },
      ],
    },
    benefits: {
      title: "Potential Benefits",
      items: [
        {
          title: "More Focus on Core Work",
          description:
            "Give internal teams more room to prioritize strategic responsibilities.",
        },
        {
          title: "Defined Workflows",
          description:
            "Document responsibilities, handoffs, and expected outputs.",
        },
        {
          title: "Operational Flexibility",
          description:
            "Review the process scope as business requirements change.",
        },
        {
          title: "Visible Performance",
          description:
            "Agree on reporting and review practices appropriate to the engagement.",
        },
      ],
    },
    closing: {
      headline: "Looking to Outsource a Business Process?",
      body: "Tell us which process you want to support, what outcomes matter, and any requirements your partner must meet.",
      cta: { label: "Discuss BPO Services", href: "/contact-us" },
    },
  },

  "bot-lean-gcc": {
    hero: {
      eyebrow: "BUILD-OPERATE-TRANSFER (BOT) / LEAN GCC",
      headline:
        "Build Your GCC. Establish Operations. Transition with Confidence.",
      description:
        "Build-Operate-Transfer (BOT) provides a structured approach to establishing a Lean Global Capability Center (GCC). With support across setup, team development, and operations, businesses can build their operational foundation, establish effective workflows, and prepare for a planned transition to their own control.",
      primaryCta: { label: "Discuss Your BOT Strategy", href: "/contact-us" },
      secondaryCta: { label: "Explore the BOT Model" },
    },
    overview: {
      heading: "A Smarter Path to Establishing Your Lean GCC",
      body: [
        "Establishing a Global Capability Center requires careful planning, the right team, and a well-defined operational structure. The Build-Operate-Transfer (BOT) model offers a structured path for organizations looking to establish their presence and capabilities while managing the complexity of building and running a new operation.",
        "Through a planned engagement, the service partner helps establish the operational foundation, develop the team, and manage day-to-day activities before transitioning the operation to the client under agreed conditions.",
        "A Lean GCC approach focuses on establishing the capabilities and operational structure required for your business, with the flexibility to develop and expand as your needs evolve.",
      ],
    },
    offerings: {
      eyebrow: "The Three Stages of BOT",
      title: "From Initial Setup to Independent Operations",
      intro:
        "The BOT model follows three key stages, each designed to support the development and transition of your Lean GCC.",
      items: [
        {
          number: "01",
          title: "Build",
          subtitle: "Establish the Foundation",
          description:
            "Define the operational scope, establish the required infrastructure and systems, and develop a team aligned with your business objectives. Build the foundation needed to support your GCC's planned activities and delivery requirements.",
        },
        {
          number: "02",
          title: "Operate",
          subtitle: "Develop and Manage Operations",
          description:
            "Run the established operation, manage workflows, and support day-to-day delivery. Refine processes, establish operational practices, and develop the team's capabilities as the GCC progresses toward the agreed transition milestones.",
        },
        {
          number: "03",
          title: "Transfer",
          subtitle: "Transition with Clarity",
          description:
            "Prepare for the planned handover of operations, responsibilities, and relevant process knowledge. Define the transition scope, readiness criteria, and handover milestones to support the client's move toward direct operational control.",
        },
      ],
    },
    process: {
      eyebrow: "Why Consider BOT / Lean GCC?",
      title: "Build the Capabilities You Need, with a Clear Path Forward",
      items: [
        {
          title: "Structured GCC Setup",
          description:
            "Establish an operational foundation through a defined setup plan covering the team, infrastructure, and activities agreed for your engagement.",
        },
        {
          title: "Focused Operational Model",
          description:
            "Develop a Lean GCC around the capabilities and operational requirements relevant to your business, with a structure that can evolve as your needs change.",
        },
        {
          title: "Supported Day-to-Day Operations",
          description:
            "Build operational maturity with support for team development, workflows, and delivery before the planned transition.",
        },
        {
          title: "Knowledge and Capability Development",
          description:
            "Develop the processes, operational understanding, and team capabilities needed to support continuity beyond the transition.",
        },
        {
          title: "Planned Transition to Ownership",
          description:
            "Define the milestones, responsibilities, and readiness criteria that guide the transfer of operations to your organization.",
        },
        {
          title: "Flexibility as Your Business Evolves",
          description:
            "Establish a foundation that can be reviewed and developed in line with changing business priorities, operational requirements, and future growth plans.",
        },
      ],
    },
    benefits: {
      eyebrow: "Engagement Approach",
      title: "Designed Around Your GCC Requirements",
      intro:
        "Every BOT / Lean GCC engagement should begin with a clear understanding of the operation you want to establish, the capabilities you need, and your long-term ownership objectives.",
      items: [
        {
          title: "Define the Scope and Location",
          description:
            "Establish the intended scope of operations, applicable geography, business requirements, and expected outcomes.",
        },
        {
          title: "Plan the Team and Infrastructure",
          description:
            "Identify staffing requirements, infrastructure, systems, and operational responsibilities needed to establish the GCC.",
        },
        {
          title: "Establish Governance and Reporting",
          description:
            "Define ownership, reporting structures, review points, performance expectations, and decision-making processes for the operating phase.",
        },
        {
          title: "Set Operational Milestones",
          description:
            "Establish a roadmap for setup, operational development, progress reviews, and transition readiness.",
        },
        {
          title: "Prepare for the Transfer",
          description:
            "Document the handover scope, timing, acceptance criteria, knowledge-transfer requirements, and responsibilities under the agreed arrangement.",
        },
      ],
    },
    closing: {
      headline: "Ready to Build Your Lean GCC?",
      body: "Explore how a Build-Operate-Transfer model can support your operational setup, team development, and transition goals. Let's discuss the capabilities you need today and how you want your operation to evolve.",
      cta: {
        label: "Discuss Your BOT / Lean GCC Strategy",
        href: "/contact-us",
      },
    },
  },
};
